
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle,
  AlertCircle,
  Plus,
  Trash2,
  AtSign,
  Phone,
  Calendar,
  Shield,
  Loader2,
  UserCog
} from "lucide-react";

interface Admin {
  id: string;
  username: string;
  email: string;
  mobile: string;
  active: boolean;
  created_at: string;
}

const Admins = () => {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [newAdmin, setNewAdmin] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    mobile: ""
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [showNewAdminDialog, setShowNewAdminDialog] = useState(false);
  const { toast } = useToast();
  const [deleteAdminId, setDeleteAdminId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('administrators')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAdmins(data || []);
    } catch (error) {
      console.error('Error fetching administrators:', error);
      toast({
        variant: "destructive",
        title: "Σφάλμα",
        description: "Αποτυχία φόρτωσης διαχειριστών.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewAdmin({ ...newAdmin, [name]: value });
    setFormError(null);
  };

  const validateForm = () => {
    if (!newAdmin.username || !newAdmin.email || !newAdmin.password || !newAdmin.mobile) {
      setFormError("Παρακαλώ συμπληρώστε όλα τα πεδία");
      return false;
    }

    if (newAdmin.password !== newAdmin.confirmPassword) {
      setFormError("Οι κωδικοί δεν ταιριάζουν");
      return false;
    }

    if (newAdmin.password.length < 6) {
      setFormError("Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newAdmin.email)) {
      setFormError("Παρακαλώ εισάγετε έγκυρο email");
      return false;
    }

    return true;
  };

  const handleCreateAdmin = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const { data, error } = await supabase.functions.invoke('create-admin', {
        body: {
          username: newAdmin.username,
          email: newAdmin.email,
          password: newAdmin.password,
          mobile: newAdmin.mobile,
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error('Σφάλμα δημιουργίας διαχειριστή');
      }

      if (!data.success) {
        setFormError(data.message || 'Σφάλμα δημιουργίας διαχειριστή');
        return;
      }

      toast({
        title: "Επιτυχία",
        description: "Ο διαχειριστής δημιουργήθηκε επιτυχώς",
      });

      setNewAdmin({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        mobile: ""
      });

      setShowNewAdminDialog(false);
      fetchAdmins();
    } catch (error) {
      console.error('Error creating administrator:', error);
      toast({
        variant: "destructive",
        title: "Σφάλμα",
        description: "Αποτυχία δημιουργίας διαχειριστή.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (adminId: string) => {
    setDeleteAdminId(adminId);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteAdminId) return;

    try {
      setIsDeleting(true);

      const { error: credError } = await supabase
        .from('admin_credentials')
        .delete()
        .eq('admin_id', deleteAdminId);

      if (credError) throw credError;

      const { error: adminError } = await supabase
        .from('administrators')
        .delete()
        .eq('id', deleteAdminId);

      if (adminError) throw adminError;

      toast({
        title: "Επιτυχία",
        description: "Ο διαχειριστής διαγράφηκε επιτυχώς",
      });

      fetchAdmins();
      setShowDeleteDialog(false);
      setDeleteAdminId(null);
    } catch (error) {
      console.error('Error deleting administrator:', error);
      toast({
        variant: "destructive",
        title: "Σφάλμα",
        description: "Αποτυχία διαγραφής διαχειριστή.",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 shadow-lg">
            <UserCog className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Διαχειριστές</h1>
            <p className="text-slate-500 mt-0.5">Διαχείριση πρόσβασης συστήματος</p>
          </div>
        </div>

        <Button onClick={() => setShowNewAdminDialog(true)} className="btn-gradient">
          <Plus className="h-4 w-4 mr-2" />
          Νέος Διαχειριστής
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-rose-50">
                <Shield className="h-5 w-5 text-rose-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{admins.length}</p>
                <p className="text-xs text-slate-500">Συνολικά</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-50">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {admins.filter(a => a.active).length}
                </p>
                <p className="text-xs text-slate-500">Ενεργοί</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-50">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {admins.filter(a => !a.active).length}
                </p>
                <p className="text-xs text-slate-500">Ανενεργοί</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {loading && (
        <div className="flex justify-center p-12">
          <div className="text-center">
            <Loader2 className="h-10 w-10 animate-spin text-rose-600 mx-auto" />
            <p className="mt-4 text-slate-500">Φόρτωση διαχειριστών...</p>
          </div>
        </div>
      )}

      {/* Admin Cards */}
      {!loading && admins.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {admins.map((admin) => (
            <Card key={admin.id} className="group border-slate-200 hover:border-rose-200 hover:shadow-lg transition-all duration-300">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center text-white font-bold text-sm">
                      {admin.username.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{admin.username}</CardTitle>
                      <Badge
                        variant={admin.active ? "default" : "secondary"}
                        className={admin.active ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : ""}
                      >
                        {admin.active ? "Ενεργός" : "Ανενεργός"}
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-2">
                <div className="flex items-center text-sm text-slate-600">
                  <AtSign className="h-4 w-4 mr-2 text-slate-400" />
                  {admin.email}
                </div>
                <div className="flex items-center text-sm text-slate-600">
                  <Phone className="h-4 w-4 mr-2 text-slate-400" />
                  {admin.mobile}
                </div>
                <div className="flex items-center text-sm text-slate-500">
                  <Calendar className="h-4 w-4 mr-2 text-slate-400" />
                  {new Date(admin.created_at).toLocaleDateString('el-GR')}
                </div>
              </CardContent>

              <CardFooter className="pt-0">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleDeleteClick(admin.id)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Διαγραφή
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      {admins.length === 0 && !loading && (
        <Card className="border-2 border-dashed border-slate-200 bg-slate-50">
          <CardContent className="py-16 text-center">
            <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-medium">Δεν υπάρχουν διαχειριστές</p>
            <p className="text-sm text-slate-400 mt-1">Προσθέστε τον πρώτο διαχειριστή</p>
          </CardContent>
        </Card>
      )}

      {/* New Admin Dialog */}
      <Dialog open={showNewAdminDialog} onOpenChange={setShowNewAdminDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Νέος Διαχειριστής</DialogTitle>
            <DialogDescription>
              Συμπληρώστε τα στοιχεία του νέου διαχειριστή
            </DialogDescription>
          </DialogHeader>

          {formError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Σφάλμα</AlertTitle>
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Όνομα χρήστη</Label>
              <Input
                id="username"
                name="username"
                value={newAdmin.username}
                onChange={handleInputChange}
                className="bg-slate-50 border-slate-200"
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={newAdmin.email}
                onChange={handleInputChange}
                className="bg-slate-50 border-slate-200"
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mobile">Κινητό</Label>
              <Input
                id="mobile"
                name="mobile"
                value={newAdmin.mobile}
                onChange={handleInputChange}
                className="bg-slate-50 border-slate-200"
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Κωδικός</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={newAdmin.password}
                onChange={handleInputChange}
                className="bg-slate-50 border-slate-200"
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Επιβεβαίωση κωδικού</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={newAdmin.confirmPassword}
                onChange={handleInputChange}
                className="bg-slate-50 border-slate-200"
                autoComplete="new-password"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewAdminDialog(false)}>
              Άκυρο
            </Button>
            <Button onClick={handleCreateAdmin} disabled={loading} className="btn-gradient">
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Δημιουργία...
                </>
              ) : (
                "Δημιουργία"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Διαγραφή Διαχειριστή</DialogTitle>
            <DialogDescription>
              Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτόν τον διαχειριστή;
              Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Άκυρο
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Διαγραφή...
                </>
              ) : (
                "Διαγραφή"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admins;
